import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import {
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  GripVertical,
  Import,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DatePicker } from '@/components/shared/DatePicker';
import { ReconciliationPanel } from './ReconciliationPanel';
import { AddressInput } from './AddressInput';
import { t } from '@/i18n';
import { useMileageApi } from '@/services/api';
import type {
  VehicleListItem,
  MileageRecordDetail,
  MileageEntryItem,
  TripCandidate,
  StandardRouteListItem,
  StructuredAddress,
  MileagePlace,
  CalculateDistanceResult,
} from '@ip/shared-types';
import { getTripDateWarning } from '@/utils/polishHolidays';

const COLUMN_COUNT = 11;

function SortableRow({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999, opacity: 0.8 } : {}),
  };
  return (
    <TableRow ref={setNodeRef} data-row-key={id} style={style} {...attributes} {...listeners}>
      {children}
    </TableRow>
  );
}

interface MonthlyMileageViewProps {
  companyNip: string;
  vehicleId: string;
  vehicles: VehicleListItem[];
  companyAddress?: StructuredAddress;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_BADGE_CLASSES: Record<string, string> = {
  finalized: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  reconciled: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400',
};

const SOURCE_BADGE_CLASSES: Record<string, string> = {
  invoice: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400',
  standard_route: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  ai_suggested: 'border-purple-500/40 bg-purple-500/10 text-purple-700 dark:text-purple-400',
};

function formatAddressDisplay(addr?: StructuredAddress): string {
  if (!addr) return '';
  const parts: string[] = [];
  if (addr.clientName) parts.push(addr.clientName);
  if (addr.city) {
    let cityPart = addr.city;
    if (addr.street) cityPart += `, ${addr.street}${addr.number ? ` ${addr.number}` : ''}`;
    parts.push(cityPart);
  }
  return parts.join(' - ') || '';
}

interface EntryFormState {
  selectedRouteId: string | undefined;
  tripDate: string;
  distanceKm: string;
  isRoundTrip: boolean;
  isEmployee: boolean;
  fromAddress: StructuredAddress;
  toAddress: StructuredAddress;
  routeDescription: string;
  purpose: string;
  driverName: string;
}

const EMPTY_FORM: EntryFormState = {
  selectedRouteId: undefined,
  tripDate: '',
  distanceKm: '',
  isRoundTrip: true,
  isEmployee: true,
  fromAddress: {},
  toAddress: {},
  routeDescription: '',
  purpose: '',
  driverName: '',
};

function StatTile({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="py-4">
      <CardContent className="px-4">
        <p className="text-muted-foreground truncate text-xs font-medium" title={title}>
          {title}
        </p>
        <div className="mt-1 font-mono text-base font-semibold tabular-nums">{children}</div>
      </CardContent>
    </Card>
  );
}

export function MonthlyMileageView({ companyNip, vehicleId, vehicles, companyAddress }: MonthlyMileageViewProps) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [record, setRecord] = useState<MileageRecordDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<MileageEntryItem | null>(null);
  const [candidateDrawerOpen, setCandidateDrawerOpen] = useState(false);
  const [candidates, setCandidates] = useState<TripCandidate[]>([]);
  const [saving, setSaving] = useState(false);
  const [routes, setRoutes] = useState<StandardRouteListItem[]>([]);
  const [places, setPlaces] = useState<MileagePlace[]>([]);
  const [searchingPlace, setSearchingPlace] = useState(false);
  const [matchedPlace, setMatchedPlace] = useState<MileagePlace | null>(null);
  const [selectedAltIdx, setSelectedAltIdx] = useState<number>(0);
  const [calculating, setCalculating] = useState(false);
  const [routeAlternatives, setRouteAlternatives] = useState<CalculateDistanceResult[]>([]);
  const [aiConfirmOpen, setAiConfirmOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [odometerEndInput, setOdometerEndInput] = useState('');
  const [form, setForm] = useState<EntryFormState>(EMPTY_FORM);
  const tripDateWarning = getTripDateWarning(form.tripDate);

  const setField = <K extends keyof EntryFormState>(field: K, value: EntryFormState[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const {
    getRecord: fetchRecord,
    updateRecord,
    createEntry,
    updateEntry,
    deleteEntry,
    reorderEntries,
    getTripCandidates,
    getRoutes,
    generateTrips,
    getPlaces,
    searchPlace,
    calculateDistanceAll,
  } = useMileageApi();

  const loadRecord = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRecord(companyNip, vehicleId, year, month);
      setRecord(data);
    } catch (error) {
      console.error('Failed to load record:', error);
      toast.error(t('errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  }, [companyNip, vehicleId, year, month, fetchRecord]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  useEffect(() => {
    getRoutes(companyNip).then(setRoutes).catch(console.error);
  }, [companyNip, getRoutes]);

  useEffect(() => {
    getPlaces(companyNip, vehicleId).then(setPlaces).catch(console.error);
  }, [companyNip, vehicleId, getPlaces]);

  useEffect(() => {
    setOdometerEndInput(record?.odometerEnd != null ? String(record.odometerEnd) : '');
  }, [record?.odometerEnd]);

  const handlePrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };

  const handleNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const handleOdometerEndChange = async (value: number | null) => {
    if (!record || value == null) return;
    try {
      await updateRecord(companyNip, record.id, { odometerEnd: value });
      loadRecord();
    } catch (error) {
      console.error('Failed to update odometer:', error);
      toast.error(t('errors.saveFailed'));
    }
  };

  const commitOdometerEnd = () => {
    if (!record) return;
    const value = Number(odometerEndInput);
    const min = record.odometerStart || 0;
    if (odometerEndInput === '' || Number.isNaN(value) || value < min) {
      // Revert invalid/out-of-range input (below odometer start is not allowed)
      setOdometerEndInput(record.odometerEnd != null ? String(record.odometerEnd) : '');
      return;
    }
    if (value !== record.odometerEnd) {
      handleOdometerEndChange(value);
    }
  };

  const handleAddEntry = () => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    const defaultDriver = vehicle?.drivers.find(d => d.isDefault) || vehicle?.drivers[0];
    setEditingEntry(null);
    setMatchedPlace(null);
    setSelectedAltIdx(0);
    setRouteAlternatives([]);
    setForm({
      ...EMPTY_FORM,
      tripDate: `${year}-${String(month).padStart(2, '0')}-01`,
      isRoundTrip: true,
      isEmployee: true,
      driverName: defaultDriver?.fullName || '',
      fromAddress: companyAddress ? { ...companyAddress } : {},
      toAddress: {},
    });
    setAddModalOpen(true);
  };

  const handleSelectRoute = (routeId: string) => {
    const route = routes.find(r => r.id === routeId);
    if (!route) return;
    setMatchedPlace(null);
    setSelectedAltIdx(0);
    setRouteAlternatives([]);
    setForm(prev => ({
      ...prev,
      selectedRouteId: routeId,
      fromAddress: route.routeFromAddress || {},
      toAddress: route.routeToAddress || {},
      distanceKm: String(route.distanceKm),
      purpose: route.defaultPurpose || '',
      isRoundTrip: route.isRoundTrip,
    }));
  };

  const handleSearchPlace = async (query: string, field: 'fromAddress' | 'toAddress') => {
    if (!query.trim()) return;
    const queryLower = query.toLowerCase().trim();

    // 1. Search local MileagePlaces first
    const placeMatch = places.find(p =>
      p.isActive && p.name.toLowerCase().includes(queryLower)
    );
    if (placeMatch) {
      const addr = {
        clientName: placeMatch.name,
        city: placeMatch.addressCity,
        street: placeMatch.addressStreet,
        number: placeMatch.addressNumber,
        country: placeMatch.addressCountry || 'Polska',
      };
      if (field === 'toAddress') {
        setForm(prev => {
          const km = prev.isRoundTrip
            ? Math.round(placeMatch.distanceKm * 2)
            : placeMatch.distanceKm;
          return {
            ...prev,
            [field]: addr,
            distanceKm: String(km),
            purpose: placeMatch.defaultPurpose || prev.purpose,
          };
        });
        setMatchedPlace(placeMatch);
        setSelectedAltIdx(0);
      } else {
        setForm(prev => ({ ...prev, [field]: addr }));
      }
      toast.success(`Found place: ${placeMatch.name}`);
      return;
    }

    // 2. Search local StandardRoutes
    const routeMatch = routes.find(r =>
      r.routeTo.toLowerCase().includes(queryLower) ||
      r.routeToAddress?.clientName?.toLowerCase().includes(queryLower)
    );
    if (routeMatch) {
      if (field === 'toAddress') {
        setForm(prev => ({
          ...prev,
          fromAddress: routeMatch.routeFromAddress || prev.fromAddress,
          toAddress: routeMatch.routeToAddress || {},
          distanceKm: String(routeMatch.distanceKm),
          purpose: routeMatch.defaultPurpose || prev.purpose,
          isRoundTrip: routeMatch.isRoundTrip,
        }));
      } else {
        setForm(prev => ({ ...prev, [field]: routeMatch.routeFromAddress || {} }));
      }
      setMatchedPlace(null);
      setSelectedAltIdx(0);
      toast.success(`Found route: ${routeMatch.routeFrom} → ${routeMatch.routeTo}`);
      return;
    }

    // 3. Fallback to Google Places API
    try {
      setSearchingPlace(true);
      const results = await searchPlace(companyNip, query);
      if (results.length === 0) {
        toast.info('No places found');
        return;
      }
      const place = results[0];
      setForm(prev => {
        const current: StructuredAddress = prev[field] || {};
        return {
          ...prev,
          [field]: {
            ...current,
            city: place.address.city || current.city,
            street: place.address.street || current.street,
            number: place.address.number || current.number,
            country: place.address.country || current.country || 'Polska',
            clientName: place.address.clientName || current.clientName,
          },
        };
      });
      setMatchedPlace(null);
      setSelectedAltIdx(0);
      toast.success(`Address found: ${place.formattedAddress}`);
    } catch (error) {
      console.error('Failed to search place:', error);
      toast.error('Failed to search place');
    } finally {
      setSearchingPlace(false);
    }
  };

  const buildGeocodableAddress = (addr: StructuredAddress): string => {
    const parts: string[] = [];
    if (addr.street) parts.push(`${addr.street}${addr.number ? ` ${addr.number}` : ''}`);
    if (addr.city) parts.push(addr.city);
    if (addr.country) parts.push(addr.country);
    return parts.join(', ');
  };

  const handleCalculateDistance = async () => {
    const fromAddr: StructuredAddress = form.fromAddress || {};
    const toAddr: StructuredAddress = form.toAddress || {};
    const origin = buildGeocodableAddress(fromAddr);
    const destination = buildGeocodableAddress(toAddr);
    if (!origin || !destination) {
      toast.warning('Enter From and To addresses first');
      return;
    }
    try {
      setCalculating(true);
      const results = await calculateDistanceAll(companyNip, {
        origin,
        destination,
        computeAlternatives: true,
      });
      setRouteAlternatives(results);
      setSelectedAltIdx(0);
      setMatchedPlace(null);
      setForm(prev => {
        const km = prev.isRoundTrip
          ? Math.round(results[0].distanceKm * 2)
          : results[0].distanceKm;
        return { ...prev, distanceKm: String(km) };
      });
      toast.success(`Found ${results.length} route(s). ${results[0].distanceKm} km one-way.`);
    } catch (error) {
      console.error('Failed to calculate distance:', error);
      toast.error('Failed to calculate distance');
    } finally {
      setCalculating(false);
    }
  };

  const handleSelectRouteAlt = (idx: number) => {
    setSelectedAltIdx(idx);
    const route = routeAlternatives[idx];
    if (route) {
      const km = form.isRoundTrip
        ? Math.round(route.distanceKm * 2)
        : route.distanceKm;
      setField('distanceKm', String(km));
    }
  };

  const handleSelectPlaceAlt = (idx: number) => {
    setSelectedAltIdx(idx);
    const alt = matchedPlace?.routeAlternatives?.[idx];
    if (alt) {
      const km = form.isRoundTrip
        ? Math.round(alt.distanceKm * 2)
        : alt.distanceKm;
      setField('distanceKm', String(km));
    }
  };

  const handleEditEntry = (entry: MileageEntryItem) => {
    setEditingEntry(entry);
    setMatchedPlace(null);
    setSelectedAltIdx(0);
    setRouteAlternatives([]);
    // Try to find matching standard route to populate addresses
    const matchingRoute = routes.find(r =>
      r.distanceKm === entry.distanceKm &&
      entry.routeDescription?.includes(r.routeFrom)
    );
    setForm({
      ...EMPTY_FORM,
      tripDate: entry.tripDate,
      routeDescription: entry.routeDescription || '',
      purpose: entry.purpose || '',
      distanceKm: String(entry.distanceKm),
      driverName: entry.driverName || '',
      isRoundTrip: entry.isRoundTrip,
      isEmployee: entry.isEmployee,
      fromAddress: matchingRoute?.routeFromAddress || {},
      toAddress: matchingRoute?.routeToAddress || {},
    });
    setAddModalOpen(true);
  };

  const handleSaveEntry = async () => {
    if (!record) return;
    if (!form.tripDate) {
      toast.error(t('employees.validation.required', { label: t('mileage.entries.tripDate') }));
      return;
    }
    const distanceKm = Number(form.distanceKm);
    if (form.distanceKm === '' || Number.isNaN(distanceKm) || distanceKm < 1) {
      toast.error(t('employees.validation.required', { label: t('mileage.entries.distanceKm') }));
      return;
    }
    if (!form.purpose.trim()) {
      toast.error(t('employees.validation.required', { label: t('mileage.entries.purpose') }));
      return;
    }
    if (!form.driverName) {
      toast.error(t('employees.validation.required', { label: t('mileage.entries.driverName') }));
      return;
    }
    try {
      setSaving(true);
      // Auto-generate routeDescription from structured addresses
      let routeDescription = form.routeDescription;
      const fromStr = formatAddressDisplay(form.fromAddress);
      const toStr = formatAddressDisplay(form.toAddress);
      if (fromStr && toStr) {
        routeDescription = form.isRoundTrip
          ? `${fromStr} - ${toStr} - ${fromStr}`
          : `${fromStr} - ${toStr}`;
      }
      const values = {
        tripDate: form.tripDate,
        routeDescription,
        purpose: form.purpose,
        distanceKm,
        driverName: form.driverName,
        isRoundTrip: form.isRoundTrip,
        isEmployee: form.isEmployee,
      };
      if (editingEntry) {
        await updateEntry(companyNip, record.id, editingEntry.id, values);
      } else {
        await createEntry(companyNip, record.id, values);
      }
      toast.success(t('common.save') + ' OK');
      setAddModalOpen(false);
      setEditingEntry(null);
      loadRecord();
    } catch (error) {
      console.error('Failed to save entry:', error);
      toast.error(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!record) return;
    try {
      await deleteEntry(companyNip, record.id, entryId);
      loadRecord();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error(t('errors.deleteFailed'));
    }
  };

  const handleAiGenerate = () => {
    if (!record || !record.odometerEnd) {
      toast.warning('Set odometer end before generating trips');
      return;
    }
    setAiConfirmOpen(true);
  };

  const handleAiGenerateConfirm = async () => {
    if (!record || generating) return;
    const vehicle = vehicles.find(v => v.id === vehicleId);
    const defaultDriver = vehicle?.drivers.find(d => d.isDefault) || vehicle?.drivers[0];
    try {
      setGenerating(true);
      const result = await generateTrips(
        companyNip,
        record.id,
        defaultDriver?.fullName || 'Kierowca',
      );
      toast.success(`Generated ${result.generatedCount} trips (${result.totalKm} km)`);
      loadRecord();
    } catch (error) {
      console.error('Failed to generate trips:', error);
      toast.error('Failed to generate trips');
    } finally {
      setGenerating(false);
    }
  };

  const handleImportCandidates = async () => {
    try {
      const data = await getTripCandidates(companyNip, vehicleId, year, month);
      setCandidates(data);
      setCandidateDrawerOpen(true);
    } catch (error) {
      console.error('Failed to load trip candidates:', error);
      toast.error(t('errors.loadingFailed'));
    }
  };

  const handleImportCandidate = async (candidate: TripCandidate) => {
    if (!record) return;
    try {
      const vehicle = vehicles.find(v => v.id === vehicleId);
      const defaultDriver = vehicle?.drivers.find(d => d.isDefault) || vehicle?.drivers[0];

      await createEntry(companyNip, record.id, {
        tripDate: candidate.invoiceDate || `${year}-${String(month).padStart(2, '0')}-15`,
        routeDescription: candidate.suggestedRoute || `Biuro - ${candidate.contractorName || 'Kontrahent'} - Biuro`,
        purpose: candidate.suggestedPurpose || `Zakup: FV ${candidate.invoiceNumber || ''}`.trim(),
        distanceKm: candidate.suggestedDistance || 10,
        driverName: defaultDriver?.fullName || '',
        invoiceId: candidate.invoiceId,
        source: 'invoice',
      });
      // Refresh candidates and record
      const [newRecord, newCandidates] = await Promise.all([
        fetchRecord(companyNip, vehicleId, year, month),
        getTripCandidates(companyNip, vehicleId, year, month),
      ]);
      setRecord(newRecord);
      setCandidates(newCandidates);
      toast.success('Trip imported');
    } catch (error) {
      console.error('Failed to import trip:', error);
      toast.error(t('errors.saveFailed'));
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !record) return;

    const entries = record.entries;
    const oldIndex = entries.findIndex(e => e.id === active.id);
    const newIndex = entries.findIndex(e => e.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Compute new order
    const reordered = [...entries];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    // Optimistic update: reassign dates to stay in chronological positions
    const sortedDates = entries.map(e => e.tripDate);
    const updatedEntries = reordered.map((e, i) => ({
      ...e, entryNumber: i + 1, tripDate: sortedDates[i],
    }));
    setRecord({ ...record, entries: updatedEntries });

    try {
      await reorderEntries(companyNip, record.id, reordered.map(e => e.id));
      loadRecord();
    } catch (error) {
      console.error('Failed to reorder entries:', error);
      toast.error(t('errors.saveFailed'));
      loadRecord(); // Revert
    }
  };

  const entriesKm = record?.entriesKm || 0;
  const targetKm = record?.odometerEnd && record?.odometerStart
    ? record.odometerEnd - record.odometerStart
    : null;
  const delta = targetKm != null ? targetKm - entriesKm : null;

  const vehicle = vehicles.find(v => v.id === vehicleId);
  const aiEntryCount = record?.entries.filter(e => e.source === 'ai_suggested').length || 0;
  const aiTargetKm = record ? (record.odometerEnd || 0) - (record.odometerStart || 0) : 0;

  const renderAltOption = (
    alt: { distanceKm: number; durationMinutes?: number; description?: string },
    idx: number,
    isDefault: boolean,
    idPrefix: string,
  ) => (
    <div key={idx} className="flex items-center gap-2">
      <RadioGroupItem value={String(idx)} id={`${idPrefix}-${idx}`} />
      <label
        htmlFor={`${idPrefix}-${idx}`}
        className="bg-card flex flex-1 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm"
      >
        <span className="font-medium">{alt.distanceKm} km</span>
        <span className="text-muted-foreground">{alt.durationMinutes} min</span>
        {alt.description && <Badge variant="outline">{alt.description}</Badge>}
        {isDefault && (
          <Badge className="border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400">
            Default
          </Badge>
        )}
      </label>
    </div>
  );

  return (
    <div>
      {/* Month navigation */}
      <div className="mb-4 flex items-center gap-4">
        <Button variant="outline" size="icon" className="size-8" onClick={handlePrevMonth}>
          <ChevronLeft className="size-4" />
        </Button>
        <span className="font-medium">
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <Button variant="outline" size="icon" className="size-8" onClick={handleNextMonth}>
          <ChevronRight className="size-4" />
        </Button>
        {vehicle && (
          <Badge variant="secondary">
            {vehicle.displayName} ({vehicle.registrationNumber})
          </Badge>
        )}
      </div>

      {/* Stats row */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatTile title={t('mileage.records.odometerStart')}>
          {record?.odometerStart ?? '-'} km
        </StatTile>
        <StatTile title={t('mileage.records.odometerEnd')}>
          <div className="flex items-center gap-1.5">
            <Input
              type="number"
              min={record?.odometerStart || 0}
              value={odometerEndInput}
              onChange={e => setOdometerEndInput(e.target.value)}
              onBlur={commitOdometerEnd}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              placeholder="Enter..."
              className="h-8 w-[120px] font-mono"
            />
            <span className="text-muted-foreground text-sm font-normal">km</span>
          </div>
        </StatTile>
        <StatTile title={t('mileage.records.entriesKm')}>
          {Math.round(entriesKm)} km
        </StatTile>
        <StatTile title={t('mileage.records.totalKm')}>
          {targetKm ?? '-'} km
        </StatTile>
        <StatTile title={t('mileage.records.delta')}>
          <span
            className={
              delta == null
                ? undefined
                : Math.abs(delta) < 5
                  ? 'text-emerald-600 dark:text-emerald-500'
                  : 'text-red-600 dark:text-red-500'
            }
          >
            {delta != null ? Math.round(delta) : '-'} km
          </span>
        </StatTile>
        <Card className="py-4">
          <CardContent className="flex h-full items-center px-4">
            <Badge
              variant={STATUS_BADGE_CLASSES[record?.status || ''] ? 'outline' : 'secondary'}
              className={STATUS_BADGE_CLASSES[record?.status || '']}
            >
              {record?.status || 'draft'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Action buttons */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleAddEntry}>
          <Plus className="size-4" />
          {t('mileage.entries.add')}
        </Button>
        <Button variant="outline" size="sm" onClick={handleImportCandidates}>
          <Import className="size-4" />
          Import from Invoices
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAiGenerate}
          disabled={generating || !record || !record.odometerEnd}
        >
          {generating ? <Loader2 className="size-4 animate-spin" /> : <Zap className="size-4" />}
          AI Generate
        </Button>
      </div>

      {/* Entries table */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={(record?.entries || []).map(e => e.id)} strategy={verticalListSortingStrategy}>
          <div className="bg-card overflow-x-auto rounded-xl border">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[30px]" />
                  <TableHead className="text-muted-foreground w-[40px] text-center">#</TableHead>
                  <TableHead className="text-muted-foreground w-[120px]">
                    {t('mileage.entries.tripDate')}
                  </TableHead>
                  <TableHead className="text-muted-foreground w-[75px] text-right">Odo Start</TableHead>
                  <TableHead className="text-muted-foreground w-[75px] text-right">Odo End</TableHead>
                  <TableHead className="text-muted-foreground w-[220px]">
                    {t('mileage.entries.routeDescription')}
                  </TableHead>
                  <TableHead className="text-muted-foreground w-[180px]">
                    {t('mileage.entries.purpose')}
                  </TableHead>
                  <TableHead className="text-muted-foreground w-[70px] text-right">
                    {t('mileage.entries.distanceKm')}
                  </TableHead>
                  <TableHead className="text-muted-foreground w-[140px]">
                    {t('mileage.entries.driverName')}
                  </TableHead>
                  <TableHead className="text-muted-foreground w-[70px]">Source</TableHead>
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }, (_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={COLUMN_COUNT}>
                        <Skeleton className="h-8 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : !record?.entries.length ? (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {record.entries.map(entry => {
                      const warning = getTripDateWarning(entry.tripDate);
                      return (
                        <SortableRow key={entry.id} id={entry.id}>
                          <TableCell className="py-2">
                            <GripVertical className="text-muted-foreground size-4 cursor-grab" />
                          </TableCell>
                          <TableCell className="py-2 text-center tabular-nums">{entry.entryNumber}</TableCell>
                          <TableCell className="py-2 whitespace-nowrap">
                            {entry.tripDate}
                            {warning && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <TriangleAlert className="ml-1 inline size-3.5 text-amber-600 dark:text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>{warning}</TooltipContent>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell className="py-2 text-right tabular-nums">
                            {entry.odometerHandover != null ? entry.odometerHandover.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell className="py-2 text-right tabular-nums">
                            {entry.odometerReturn != null ? entry.odometerReturn.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell className="max-w-[220px] truncate py-2" title={entry.routeDescription}>
                            {entry.routeDescription}
                          </TableCell>
                          <TableCell className="max-w-[180px] truncate py-2" title={entry.purpose}>
                            {entry.purpose}
                          </TableCell>
                          <TableCell className="py-2 text-right tabular-nums">{entry.distanceKm}</TableCell>
                          <TableCell className="py-2">{entry.driverName}</TableCell>
                          <TableCell className="py-2">
                            <Badge
                              variant={SOURCE_BADGE_CLASSES[entry.source] ? 'outline' : 'secondary'}
                              className={SOURCE_BADGE_CLASSES[entry.source]}
                            >
                              {entry.source}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2">
                            <div className="flex items-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8"
                                onClick={() => handleEditEntry(entry)}
                              >
                                <Pencil className="size-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-destructive size-8">
                                    <Trash2 className="size-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete entry?</AlertDialogTitle>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-white hover:bg-destructive/90"
                                      onClick={() => handleDeleteEntry(entry.id)}
                                    >
                                      {t('common.delete')}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </SortableRow>
                      );
                    })}
                    <TableRow className="bg-muted/50 font-medium hover:bg-muted/50">
                      <TableCell colSpan={7} className="py-2 text-right">
                        Total:
                      </TableCell>
                      <TableCell className="py-2 text-right tabular-nums">
                        {Math.round(entriesKm)}
                      </TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </SortableContext>
      </DndContext>

      {/* Reconciliation panel */}
      {record && (
        <ReconciliationPanel
          companyNip={companyNip}
          record={record}
          vehicleId={vehicleId}
          vehicles={vehicles}
          onEntryCreated={loadRecord}
        />
      )}

      {/* Add entry modal */}
      <Dialog
        open={addModalOpen}
        onOpenChange={open => {
          setAddModalOpen(open);
          if (!open) setEditingEntry(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? t('mileage.entries.edit') : t('mileage.entries.add')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {routes.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="entry-route">Standard Route</Label>
                <Select value={form.selectedRouteId ?? ''} onValueChange={handleSelectRoute}>
                  <SelectTrigger id="entry-route" className="w-full">
                    <SelectValue placeholder="Select a standard route to auto-fill..." />
                  </SelectTrigger>
                  <SelectContent>
                    {routes.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {`${r.routeFrom} → ${r.routeTo} (${r.distanceKm} km)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1.5">
                <Label>{t('mileage.entries.tripDate')}</Label>
                <DatePicker
                  value={form.tripDate ? dayjs(form.tripDate).toDate() : null}
                  onChange={date => setField('tripDate', date ? dayjs(date).format('YYYY-MM-DD') : '')}
                  className="w-[150px]"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="entry-distance">{t('mileage.entries.distanceKm')}</Label>
                <div className="flex w-[120px]">
                  <Input
                    id="entry-distance"
                    type="number"
                    min={1}
                    step={1}
                    className="rounded-r-none"
                    value={form.distanceKm}
                    onChange={e => setField('distanceKm', e.target.value)}
                  />
                  <span className="border-input bg-muted text-muted-foreground inline-flex items-center rounded-r-md border border-l-0 px-3 text-sm">
                    km
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="entry-round-trip">{t('mileage.entries.isRoundTrip')}</Label>
                <div className="flex h-9 items-center">
                  <Switch
                    id="entry-round-trip"
                    checked={form.isRoundTrip}
                    onCheckedChange={checked => setField('isRoundTrip', checked)}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mb-0.5"
                onClick={handleCalculateDistance}
                disabled={calculating}
              >
                {calculating ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <MapPin className="size-4" />
                )}
                {t('mileage.routes.calculateDistance')}
              </Button>
            </div>
            {tripDateWarning && (
              <p className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-500">
                <TriangleAlert className="size-4" /> {tripDateWarning}
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <Label>From</Label>
              <AddressInput
                value={form.fromAddress}
                onChange={value => setField('fromAddress', value)}
                showClientName
                defaultAddress={companyAddress}
                onSearchPlace={q => handleSearchPlace(q, 'fromAddress')}
                searchLoading={searchingPlace}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>To</Label>
              <AddressInput
                value={form.toAddress}
                onChange={value => setField('toAddress', value)}
                showClientName
                defaultAddress={companyAddress}
                onSearchPlace={q => handleSearchPlace(q, 'toAddress')}
                searchLoading={searchingPlace}
              />
            </div>

            {/* Route alternatives from matched MileagePlace */}
            {matchedPlace?.routeAlternatives && matchedPlace.routeAlternatives.length > 1 && (
              <div className="flex flex-col gap-1.5">
                <Label>Route options (saved)</Label>
                <RadioGroup
                  value={String(selectedAltIdx)}
                  onValueChange={v => handleSelectPlaceAlt(Number(v))}
                  className="gap-2"
                >
                  {matchedPlace.routeAlternatives.map((alt, idx) =>
                    renderAltOption(alt, idx, !!alt.isDefault, 'place-alt'),
                  )}
                </RadioGroup>
              </div>
            )}

            {/* Route alternatives from Google Maps distance calculation */}
            {routeAlternatives.length > 1 && (
              <div className="flex flex-col gap-1.5">
                <Label>Route options</Label>
                <RadioGroup
                  value={String(selectedAltIdx)}
                  onValueChange={v => handleSelectRouteAlt(Number(v))}
                  className="gap-2"
                >
                  {routeAlternatives.map((alt, idx) =>
                    renderAltOption(alt, idx, idx === 0, 'route-alt'),
                  )}
                </RadioGroup>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="entry-route-description" className="flex items-center gap-1">
                {t('mileage.entries.routeDescription')}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleHelp className="text-muted-foreground size-3.5" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Auto-generated from addresses above. Override manually if needed.
                  </TooltipContent>
                </Tooltip>
              </Label>
              <Input
                id="entry-route-description"
                placeholder="Auto-generated from From/To addresses"
                value={form.routeDescription}
                onChange={e => setField('routeDescription', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="entry-purpose">{t('mileage.entries.purpose')}</Label>
              <Input
                id="entry-purpose"
                placeholder="Zakup materiałów biurowych"
                value={form.purpose}
                onChange={e => setField('purpose', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="entry-driver">{t('mileage.entries.driverName')}</Label>
              <Select value={form.driverName} onValueChange={v => setField('driverName', v)}>
                <SelectTrigger id="entry-driver" className="w-full">
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  {(vehicle?.drivers || []).map(d => (
                    <SelectItem key={d.fullName} value={d.fullName}>
                      {d.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setAddModalOpen(false); setEditingEntry(null); }}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={saving} onClick={handleSaveEntry}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate confirmation */}
      <AlertDialog open={aiConfirmOpen} onOpenChange={setAiConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>AI Generate Trips</AlertDialogTitle>
            <AlertDialogDescription>
              {aiEntryCount > 0
                ? `This will remove ${aiEntryCount} AI-generated entries and create new trips to fill ${aiTargetKm} km. Continue?`
                : `This will generate trips to fill ${aiTargetKm} km. Continue?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleAiGenerateConfirm}>Generate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Trip candidates drawer */}
      <Sheet open={candidateDrawerOpen} onOpenChange={setCandidateDrawerOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-[520px]">
          <SheetHeader>
            <SheetTitle>Invoice Trip Candidates</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-2 px-4 pb-4">
            {candidates.length === 0 ? (
              <span className="text-muted-foreground text-sm">
                No trip candidates found for this month.
              </span>
            ) : (
              candidates.map(c => (
                <Card key={c.invoiceId} className="py-3">
                  <CardContent className="flex flex-col items-start gap-1 px-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{c.contractorName || 'Unknown'}</span>
                      <Badge
                        variant="outline"
                        className={
                          c.reason === 'by_car_contractor'
                            ? 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400'
                            : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                        }
                      >
                        {c.reason === 'by_car_contractor' ? 'By Car' :
                         c.reason === 'cash_payment' ? 'Cash' : 'Card'}
                      </Badge>
                      {c.alreadyLinked && (
                        <Badge className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                          Linked
                        </Badge>
                      )}
                    </div>
                    <span className="text-muted-foreground text-sm">
                      {c.invoiceNumber} | {c.invoiceDate} | {c.amount?.toFixed(2)} PLN
                    </span>
                    {c.suggestedRoute && (
                      <span className="text-muted-foreground text-sm">
                        Route: {c.suggestedRoute} ({c.suggestedDistance} km)
                      </span>
                    )}
                    {!c.alreadyLinked && (
                      <Button size="sm" className="mt-1" onClick={() => handleImportCandidate(c)}>
                        Import
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
